/*
 * Superpets cart drawer.
 *
 * Every handler is delegated from `document`, because the drawer's contents are
 * re-rendered after each cart change — listeners bound to elements inside it
 * would die on the first update.
 *
 * Money is never calculated here. Each mutation goes to Shopify, then the
 * section is re-rendered by Liquid, so prices, discounts and currency
 * formatting stay authoritative.
 */
(() => {
  const SECTION = 'sp-cart-drawer';
  const OPEN_ATTR = 'data-sp-cart-open';

  const root = () => document.documentElement;
  const wrapper = () => document.getElementById(`shopify-section-${SECTION}`);
  const drawer = () => document.querySelector('[data-sp-cart]');

  let lastFocused = null;

  /* ---------- open / close ---------- */

  function open() {
    if (root().hasAttribute(OPEN_ATTR)) return;
    lastFocused = document.activeElement;
    root().setAttribute(OPEN_ATTR, '');
    const panel = document.querySelector('[data-sp-cart-panel]');
    if (panel) panel.focus({ preventScroll: true });
  }

  function close() {
    if (!root().hasAttribute(OPEN_ATTR)) return;
    root().removeAttribute(OPEN_ATTR);
    // Returning focus matters for keyboard users: without it focus falls back
    // to <body> and the next Tab starts from the top of the page.
    if (lastFocused && document.contains(lastFocused)) lastFocused.focus({ preventScroll: true });
    lastFocused = null;
  }

  function busy(state) {
    const el = drawer();
    if (el) el.classList.toggle('is-busy', state);
  }

  /* ---------- rendering ---------- */

  // The cart page renders the same line items, so a change made in the drawer
  // has to update both or the page behind it goes stale. It is only requested
  // when it's actually on screen.
  //
  // Its id is read off the DOM rather than assumed: the drawer is a static
  // section so its id is the file name, but the cart page comes from a JSON
  // template where the id is whatever key the template used ("cart-section"
  // here). Hardcoding the file name silently no-ops the refresh.
  function pageSectionId() {
    const host = document.querySelector('.sp-cartpage')?.closest('.shopify-section');
    return host?.id?.replace('shopify-section-', '') || null;
  }

  function targets() {
    const ids = [SECTION];
    const page = pageSectionId();
    if (page) ids.push(page);
    return ids;
  }

  // The regions of the drawer that a cart change can rewrite. Everything else —
  // the drawer itself, the scrim, the panel — is structure, and stays put.
  const DRAWER_REGIONS = ['.sp-cart__head', '.sp-cart__ship', '.sp-cart__promo', '.sp-cart__upsell', '.sp-cart__foot'];

  const lineKeys = (node) => [...node.querySelectorAll('[data-sp-line]')].map((el) => el.dataset.spLine).join(',');

  // A quantity step changes numbers, not shape. Rebuilding the panel for it is
  // what makes the drawer flash on iOS: on a phone .sp-cart__panel is the whole
  // screen (`width: min(460px, 100%)`), so replacing it throws away the
  // compositing layer covering the viewport and Safari can present a frame
  // before the replacement has been painted — the screen dims and comes back.
  // Desktop browsers repaint it within the frame, which is why this only shows
  // up on a phone.
  //
  // So the panel keeps its element, its rows keep their images, and only the
  // parts Liquid actually rewrote are replaced. Every value still comes from the
  // rendered section — nothing here recalculates money.
  //
  // Returns false when the cart's shape changed (a line gone, the upsell
  // appearing, the last item removed); the caller then does the plain refill,
  // where a repaint is both unavoidable and expected.
  function morphPanel(live, fresh) {
    if (lineKeys(live) !== lineKeys(fresh)) return false;

    for (const selector of DRAWER_REGIONS) {
      const here = live.querySelector(selector);
      const there = fresh.querySelector(selector);
      // One side has a region the other doesn't: the panel's shape moved.
      if (!here !== !there) return false;
      if (here && there && here.innerHTML !== there.innerHTML) here.innerHTML = there.innerHTML;
    }

    // Rows are updated through .sp-cart__info so the thumbnail's <img> is never
    // re-created — a re-inserted image is a second chance to flash.
    live.querySelectorAll('[data-sp-line]').forEach((row) => {
      const match = fresh.querySelector(`[data-sp-line="${row.dataset.spLine}"]`);
      if (!match) return;
      const info = row.querySelector('.sp-cart__info');
      const freshInfo = match.querySelector('.sp-cart__info');
      if (info && freshInfo && info.innerHTML !== freshInfo.innerHTML) info.innerHTML = freshInfo.innerHTML;
    });

    return true;
  }

  function swapDrawer(host, fresh) {
    const live = host.querySelector('[data-sp-cart]');
    const next = fresh.querySelector('[data-sp-cart]');
    if (!live || !next) return false;

    const panel = live.querySelector('[data-sp-cart-panel]');
    const nextPanel = next.querySelector('[data-sp-cart-panel]');
    if (!panel || !nextPanel) return false;

    // Both of these live on the element being kept: the count is what syncCount
    // reads, and the accent colour is an inline custom property.
    live.dataset.cartCount = next.dataset.cartCount || '0';
    live.setAttribute('style', next.getAttribute('style') || '');

    if (morphPanel(panel, nextPanel)) return true;

    // The panel is the scroll container, and refilling it collapses its content
    // for an instant, which clamps scrollTop to 0 — a shopper removing a line
    // halfway down a long cart would be thrown back to the top.
    const scrolled = panel.scrollTop;
    panel.innerHTML = nextPanel.innerHTML;
    panel.scrollTop = scrolled;
    return true;
  }

  function swap(id, html) {
    const host = document.getElementById(`shopify-section-${id}`);
    if (!host || !html) return;
    // The response includes the section wrapper itself; take its innards so we
    // don't nest a second wrapper on every refresh.
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.getElementById(`shopify-section-${id}`) || parsed.body;

    if (id === SECTION && swapDrawer(host, fresh)) return;

    // The cart page updates piecewise. Shopify injects the Shop Pay / PayPal
    // buttons into the checkout form after load, and Liquid re-renders that
    // container empty — replacing the whole section deletes them permanently.
    // Emptying the cart changes the page's shape, so that case falls through to
    // a full replace (there is no express button to preserve at zero items).
    const parts = host.querySelectorAll('[data-sp-page-part]');
    const freshParts = fresh.querySelectorAll('[data-sp-page-part]');
    if (parts.length && freshParts.length === parts.length) {
      parts.forEach((node) => {
        const match = fresh.querySelector(`[data-sp-page-part="${node.dataset.spPagePart}"]`);
        if (match) node.innerHTML = match.innerHTML;
      });
      return;
    }

    host.innerHTML = fresh.innerHTML;
  }

  // `current` lets a caller cancel its own repaint. A render describes the cart
  // as it was when requested, so one still in flight when the shopper clicks
  // again would repaint the older number over the newer one — the flicker of a
  // quantity appearing to go backwards before settling.
  async function refresh(current) {
    const ids = targets();
    const url = `${window.Shopify?.routes?.root || '/'}?sections=${ids.join(',')}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Section render failed: ${res.status}`);
    const data = await res.json();
    if (!data[SECTION]) throw new Error('Section render returned no markup');
    if (current && !current()) return;

    ids.forEach((id) => swap(id, data[id]));
    syncCount();
  }

  // The header lives in a different section, so its badge is updated directly
  // rather than re-rendering the whole header for one number.
  function syncCount() {
    const el = drawer();
    if (!el) return;
    const count = Number(el.dataset.cartCount || 0);
    const link = document.querySelector('.sp-header__cart');
    if (!link) return;

    let badge = link.querySelector('.sp-header__cart-count');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'sp-header__cart-count';
        link.appendChild(badge);
      }
      badge.textContent = count;
    } else if (badge) {
      badge.remove();
    }
    link.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
  }

  /* ---------- cart mutations ---------- */

  async function post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || `Cart request failed: ${res.status}`);
    }
    return res.json();
  }

  // The first-order gifts the product page lists, as variant ids. Appstle's
  // discount makes these lines free when they carry the two properties below,
  // which is exactly what its own widget posts.
  // The page repeats the gift tiles inside every plan card, so the same gift
  // appears once per card. Add each gift once; Shopify merges duplicate lines
  // into one with a higher quantity and Appstle only makes the first free.
  function giftItems(variant, plan) {
    const ids = [...new Set([...document.querySelectorAll('[data-sp-gift]')].map((el) => el.dataset.spGift).filter(Boolean))];
    return ids.map((id) => ({
      id,
      quantity: 1,
      properties: { _appstle_parent_product: variant, _appstle_parent_selling_plan: plan },
    }));
  }

  async function addFromForm(form) {
    const data = new FormData(form);
    const plan = data.get('selling_plan');
    const gifts = plan ? giftItems(data.get('id'), plan) : [];
    // /cart/add.js accepts form data directly, which keeps selling plans,
    // line item properties and quantity intact without re-deriving them.
    // Subscribing with gifts on the page is the one case that needs the JSON
    // shape, so the product and its gifts land in a single request.
    const res = gifts.length
      ? await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: data.get('id'), quantity: Number(data.get('quantity') || 1), selling_plan: plan }, ...gifts],
          }),
        })
      : await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: data,
        });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || `Add to cart failed: ${res.status}`);
    }
    return res.json();
  }

  // Lines are addressed by their key, not their 1-based position. Position
  // shifts the moment any line is removed, and removal no longer freezes the
  // panel while it waits — so a shopper can now click remove on two rows in
  // quick succession, and the second click would have carried an index that had
  // already moved under it and deleted the wrong product. change.js takes the
  // key as `id`, and a key doesn't move.
  const changeLine = (key, quantity) => post(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { id: key, quantity });

  /* ---------- optimistic quantity ---------- */

  // Paint the new quantity straight away and re-point the row's own steppers,
  // so a second click computes from what is on screen rather than from markup
  // that hasn't been re-rendered yet.
  function stepRow(button, quantity) {
    const row = button.closest('[data-sp-line], tr, li');
    if (!row) return;

    const count = row.querySelector('.sp-cart__count, .sp-cartpage__num');
    if (count) count.textContent = String(quantity);
    row.classList.add('is-updating');

    row.querySelectorAll('[data-sp-cart-qty]').forEach((step) => {
      const decreases = (step.getAttribute('aria-label') || '').toLowerCase().startsWith('decrease');
      step.dataset.spQtyValue = String(decreases ? quantity - 1 : quantity + 1);
    });
  }

  // A line on its way out leaves the layout at once rather than sitting there
  // through the round trip. The last line is the exception: hiding it would
  // leave a panel with no rows and no empty-cart message until the re-render
  // arrives, which reads as broken, so that one softens in place.
  function markRemoving(row) {
    if (!row) return;
    const siblings = row.parentElement
      ? row.parentElement.querySelectorAll('[data-sp-line], tr, li').length
      : 1;
    row.classList.add(siblings > 1 ? 'is-removing' : 'is-removing-last');
  }

  // Changes run one at a time, and only the last one in a burst pays for a
  // re-render — clicking + four times quickly shouldn't fetch the section
  // four times.
  let chain = Promise.resolve();
  let pending = 0;
  let version = 0;

  function enqueue(line, quantity, row) {
    const stamp = ++version;
    pending += 1;
    chain = chain
      .then(async () => {
        await changeLine(line, quantity);
        if (pending === 1) await refresh(() => stamp === version);
      })
      .catch(async (error) => {
        console.error('[sp-cart]', error);
        // The optimistic number is now a guess that may be wrong; put the
        // authoritative markup back.
        if (pending === 1) await refresh().catch(() => {});
      })
      .finally(() => {
        pending -= 1;
        // Only reached when the row survived — a successful change re-renders
        // the markup out from under it. So this is the restore path: a failed
        // remove puts the row back on screen rather than leaving it hidden.
        if (pending === 0 && row && row.isConnected) {
          row.classList.remove('is-updating', 'is-removing', 'is-removing-last');
        }
      });
  }

  /* ---------- events ---------- */

  async function handleAdd(form) {
    const submit = form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;

    try {
      await addFromForm(form);
      await refresh();
      open();
    } catch (error) {
      // Fall back to the normal POST so a shopper is never stranded with a
      // dead button because of a network blip.
      console.error('[sp-cart]', error);
      form.submit();
      return;
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  // Safety net for anything that submits the form without a click on its
  // button (requestSubmit, a browser that submits on Enter without one).
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    handleAdd(form);
  });

  document.addEventListener('click', async (event) => {
    const cartLink = event.target.closest('.sp-header__cart');
    if (cartLink) {
      event.preventDefault();
      open();
      return;
    }

    // The add is taken at the button click, before the form's submit event can
    // fire. Shopify's analytics listens for that submit on /cart/add forms and
    // reports its own add-to-cart at the variant's one-time price, on top of
    // the events for the lines /cart/add.js really added — so Meta counted a
    // one-time purchase next to every subscription. Enter in a field clicks the
    // submit button too, so the keyboard path comes through here as well.
    const add = event.target.closest('form[action*="/cart/add"] [type="submit"]');
    if (add) {
      event.preventDefault();
      if (add.form.reportValidity()) handleAdd(add.form);
      return;
    }

    if (event.target.closest('[data-sp-cart-close]')) {
      close();
      return;
    }

    const qty = event.target.closest('[data-sp-cart-qty]');
    if (qty) {
      // On the cart page these are real submit buttons — stop the form post,
      // since we're about to do the same change over fetch.
      event.preventDefault();
      const line = qty.dataset.spCartQty;
      const quantity = Math.max(0, Number(qty.dataset.spQtyValue));

      // The number moves now, not after two round trips. Stepping used to dim
      // the whole panel and block pointers while /cart/change.js and the
      // section render came back, so a click read as: freeze, fade, then jump.
      // Shopify still has the last word — the re-render overwrites whatever is
      // shown here.
      const row = qty.closest('[data-sp-line], tr, li');
      if (quantity > 0) stepRow(qty, quantity);
      // Stepping the last one down to zero is a removal, so it gets the same
      // treatment as pressing remove — otherwise the row sat there still
      // reading "1" until the re-render took it away.
      else markRemoving(row);

      enqueue(line, quantity, row);
      return;
    }

    // "Subscribe and Save" tick box on a one-time line: move the line onto the
    // plan, then add the welcome-kit gifts if the page lists them.
    const sub = event.target.closest('[data-sp-cart-subscribe]');
    if (sub) {
      sub.disabled = true;
      const row = sub.closest('[data-sp-line], tr, li');
      row?.classList.add('is-updating');
      const root = window.Shopify?.routes?.root || '/';
      try {
        await post(`${root}cart/change.js`, { id: sub.dataset.spCartSubscribe, quantity: Number(sub.dataset.qty) || 1, selling_plan: sub.dataset.plan });
        const gifts = giftItems(sub.dataset.variant, sub.dataset.plan);
        if (gifts.length) await post(`${root}cart/add.js`, { items: gifts });
        await refresh();
      } catch (error) {
        console.error('[sp-cart]', error);
        sub.disabled = false;
        sub.checked = false;
        row?.classList.remove('is-updating');
      }
      return;
    }

    const remove = event.target.closest('[data-sp-cart-remove]');
    if (remove) {
      event.preventDefault();

      // The row goes now, not after two round trips. This used to dim and
      // freeze the whole panel while /cart/change.js and the section render
      // came back, so removing one line read as: freeze, fade, then the row
      // disappears anyway. Same reasoning as stepping a quantity.
      const row = remove.closest('[data-sp-line], tr, li');
      markRemoving(row);

      enqueue(remove.dataset.spCartRemove, 0, row);
      return;
    }

    const arrow = event.target.closest('[data-sp-rail]');
    if (arrow) {
      // Scroll by roughly one card rather than a fixed pixel count, so the step
      // still makes sense when the rail is resized or a card drops out.
      const track = arrow.parentElement.querySelector('[data-sp-rail-track]');
      if (track) {
        const card = track.querySelector('li');
        const step = card ? card.getBoundingClientRect().width + 24 : track.clientWidth / 2;
        track.scrollBy({ left: arrow.dataset.spRail === 'prev' ? -step : step, behavior: 'smooth' });
      }
      return;
    }

    const upsell = event.target.closest('[data-sp-cart-add]');
    if (upsell) {
      busy(true);
      try {
        await post(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          items: [{ id: Number(upsell.dataset.spCartAdd), quantity: 1 }],
        });
        await refresh();
      } catch (error) {
        console.error('[sp-cart]', error);
      } finally {
        busy(false);
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!root().hasAttribute(OPEN_ATTR)) return;

    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    // Focus trap: a modal that lets Tab wander behind the scrim is unusable
    // with a keyboard and invisible to a screen reader user.
    const panel = document.querySelector('[data-sp-cart-panel]');
    if (!panel) return;
    const focusable = panel.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  syncCount();
})();
